import { ClockPort, LoggerPort } from './ports/ports.types';
import {
  DEFAULT_MIN_RENEW_INTERVAL_MS,
  DEFAULT_RENEW_CHECK_INTERVAL_MS,
  DEFAULT_RENEW_RETRY_DELAYS_MS,
} from './auth-core.const';

export type RenewalSchedulerOptions = {
  /** True when a credential is held **and** it has entered its renewal window. */
  isDue: () => boolean;
  /** Attempt one renewal. Must reject on failure for the retry to engage. */
  renew: () => Promise<unknown>;
  /** Called once, after the final attempt fails. */
  onExhausted: (error: unknown) => void;
  clock: ClockPort;
  logger: LoggerPort;
  intervalMs?: number;
  retryDelaysMs?: readonly number[];
  /** Floor between successful renewals, guarding against a renewal storm. */
  minIntervalMs?: number;
  /** Failures that retrying cannot fix. They skip straight to `onExhausted`. */
  isTerminal?: (error: unknown) => boolean;
};

/**
 * Decides *when* to renew. The client decides *how*.
 *
 * This exists as one mechanism owned by the client rather than a timer inside
 * each credential source, which fixes three faults at once:
 *
 * - `OidcCredentialSource` used to renew off the library's `accessTokenExpiring`
 *   event by calling its own `renew()`, bypassing the client's single-flight.
 *   A scheduled renewal could therefore run concurrently with a 401-triggered
 *   one and spend the same refresh token twice — which, under IdentityServer's
 *   default one-time-use rotation, invalidates the whole chain.
 * - that event fires **once** per token and is only re-armed on `userLoaded`,
 *   so a single failed attempt meant no further scheduled renewal ever. Polling
 *   retries by construction.
 * - `HostCredentialSource` documented expiry as "a scheduled event rather than a
 *   surprise 401" but scheduled nothing at all. It now inherits this.
 */
export class RenewalScheduler {
  private readonly retryDelaysMs: readonly number[];
  private readonly intervalMs: number;
  private readonly minIntervalMs: number;

  private timer: ReturnType<typeof setInterval> | null = null;
  private attempting = false;
  private failureCount = 0;
  private nextAttemptAtMs = 0;
  private lastSuccessAtMs = 0;

  constructor(private readonly options: RenewalSchedulerOptions) {
    this.retryDelaysMs = options.retryDelaysMs ?? DEFAULT_RENEW_RETRY_DELAYS_MS;
    this.intervalMs = options.intervalMs ?? DEFAULT_RENEW_CHECK_INTERVAL_MS;
    this.minIntervalMs = options.minIntervalMs ?? DEFAULT_MIN_RENEW_INTERVAL_MS;
  }

  start(): void {
    if (this.timer) return;
    this.timer = setInterval(() => {
      // `tick` handles its own failures; the guard is for anything unforeseen,
      // because an unhandled rejection here would kill the renewal loop.
      this.tick().catch(() => undefined);
    }, this.intervalMs);
  }

  stop(): void {
    if (!this.timer) return;
    clearInterval(this.timer);
    this.timer = null;
  }

  /**
   * Clears the failure state. Called whenever a credential arrives, so a token
   * obtained by any route — a successful retry, the 401 path, a host push —
   * re-enables scheduling after attempts were exhausted.
   */
  reset(): void {
    this.failureCount = 0;
    this.nextAttemptAtMs = 0;
  }

  private async tick(): Promise<void> {
    if (!this.shouldAttempt()) return;

    this.attempting = true;
    try {
      await this.options.renew();
      this.recordSuccess();
    } catch (error) {
      this.recordFailure(error);
    } finally {
      this.attempting = false;
    }
  }

  private shouldAttempt(): boolean {
    if (this.attempting) return false;
    if (this.isExhausted()) return false;

    const now = this.options.clock.now();
    if (now < this.nextAttemptAtMs) return false;
    // Floor between *successful* renewals. Without it, an access token whose
    // lifetime is shorter than the renewal skew is due again the instant it
    // arrives, and the scheduler renews on every tick — a token-endpoint storm
    // that, with one-time-use refresh tokens, also rotates the token 12 times a
    // minute. Cheap insurance against a config nobody meant to write.
    if (now - this.lastSuccessAtMs < this.minIntervalMs) return false;

    return this.options.isDue();
  }

  private recordSuccess(): void {
    this.reset();
    this.lastSuccessAtMs = this.options.clock.now();

    if (this.options.isDue()) {
      this.options.logger.error(
        'Renewed credential is already due for renewal — the access token lifetime ' +
          'is at or below the renewal skew, so renewal cannot get ahead of expiry',
        { minIntervalMs: this.minIntervalMs },
      );
      return;
    }

    this.options.logger.debug('Credential renewed');
  }

  private isExhausted(): boolean {
    return this.failureCount > this.retryDelaysMs.length;
  }

  private recordFailure(error: unknown): void {
    this.failureCount = this.options.isTerminal?.(error)
      ? this.retryDelaysMs.length + 1
      : this.failureCount + 1;

    if (this.isExhausted()) {
      this.options.onExhausted(error);
      return;
    }

    const delayMs = this.retryDelaysMs[this.failureCount - 1];
    this.nextAttemptAtMs = this.options.clock.now() + delayMs;
    this.options.logger.warn('Renewal attempt failed; will retry', {
      attempt: this.failureCount,
      totalAttempts: this.retryDelaysMs.length + 1,
      retryInMs: delayMs,
      error,
    });
  }
}
